import React, { useState, useEffect, useCallback } from 'react';
import styles from './UsersList.module.css';
import { GET } from '../../../../config/api';

const formatLastSeenHours = lastOnline => {
  if (!lastOnline) return '❌';
  const diffMs = Date.now() - new Date(lastOnline);
  if (Number.isNaN(diffMs) || diffMs < 0) return '❌';
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (hours < 1) return '<1h ago';
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

const isWithinHours = (lastOnline, hoursLimit) => {
  if (!lastOnline) return false;
  const diffHours = (Date.now() - new Date(lastOnline)) / (1000 * 60 * 60);
  return diffHours <= hoursLimit;
};

// Calculate hourly user activity for the last 24 hours
const calculateHourlyActivity = (users) => {
  const now = new Date();
  const hours = [];
  const nowTime = now.getTime();
  const twentyFourHoursAgo = nowTime - (24 * 60 * 60 * 1000);
  
  // Initialize 24 hours (from 23 hours ago to now)
  for (let i = 23; i >= 0; i--) {
    const hourStart = new Date(now);
    hourStart.setHours(now.getHours() - i, 0, 0, 0);
    hourStart.setMinutes(0, 0, 0);
    const hourEnd = new Date(hourStart);
    hourEnd.setHours(hourEnd.getHours() + 1);
    
    const hourStartTime = hourStart.getTime();
    const hourEndTime = hourEnd.getTime();
    
    hours.push({
      hour: hourStart.getHours(),
      label: hourStart.toLocaleTimeString('en-US', { hour: '2-digit', hour12: true }),
      startTime: hourStartTime,
      endTime: hourEndTime,
      count: 0,
    });
  }
  
  // Count users active in each hour
  // A user is counted in an hour if their lastOnline falls within that hour
  users.forEach(user => {
    if (!user.lastOnline) return;
    const lastOnlineTime = new Date(user.lastOnline).getTime();
    
    // Only count if user was active within the last 24 hours
    if (lastOnlineTime < twentyFourHoursAgo) return;
    
    hours.forEach(hourData => {
      // Check if user's lastOnline falls within this hour window
      if (lastOnlineTime >= hourData.startTime && lastOnlineTime < hourData.endTime) {
        hourData.count++;
      }
    });
  });
  
  return hours;
};

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectedUsers, setConnectedUsers] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    verified: 'all',
    fcm: 'all',
    lastSeen: 'all',
    platform: 'all',
    dateFrom: '',
    dateTo: '',
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const {data, error, message} = await GET('website/admin/users');
    if (data) {
      // Sort users by lastOnline timestamp (most recent first)
      const sortedUsers = [...data.users].sort((a, b) => {
        if (!a.lastOnline) return 1; // Users without lastOnline go to the end
        if (!b.lastOnline) return -1;
        return new Date(b.lastOnline) - new Date(a.lastOnline);
      });
      setUsers(sortedUsers);
      const connectedUsers = Object.keys(data.connectedUsers).map(userId => {
        const match = sortedUsers.find(user => user._id === userId);
        return {username: match?.username || 'Unknown'};
      });
      setConnectedUsers(connectedUsers);
    }
    error && setError(message);
    setLoading(false);
  }, []);

  // Fetch users from an API when the component mounts
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter(user => {
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const haystack = [
        user.username,
        user.email,
        user.firstName,
        user.lastName,
        user.appVersion,
        user.platform,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(term)) return false;
    }

    if (filters.verified !== 'all') {
      const shouldBeVerified = filters.verified === 'verified';
      if (!!user.verified !== shouldBeVerified) return false;
    }

    if (filters.fcm !== 'all') {
      const hasTokens = (user.fcmTokens?.length ?? 0) > 0;
      if (filters.fcm === 'with' && !hasTokens) return false;
      if (filters.fcm === 'without' && hasTokens) return false;
    }

    if (filters.lastSeen !== 'all') {
      const hoursLookup = {
        '1h': 1,
        '4h': 4,
        '12h': 12,
        '24h': 24,
      };
      const limit = hoursLookup[filters.lastSeen];
      if (!isWithinHours(user.lastOnline, limit)) return false;
    }

    if (filters.platform !== 'all') {
      const platform = (user.platform || '').toLowerCase();
      if (filters.platform === 'web') {
        // Match any platform that starts with "web" (e.g., "web chrome", "web firefox")
        if (!platform.startsWith('web')) return false;
      } else {
        // Exact match for other platforms (ios, android)
        if (platform !== filters.platform) return false;
      }
    }

    // Date range filter
    if (filters.dateFrom || filters.dateTo) {
      if (!user.lastOnline) return false; // Users without lastOnline are excluded when date filter is active
      
      const lastOnlineDate = new Date(user.lastOnline);
      const lastOnlineTime = lastOnlineDate.getTime();
      
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0); // Start of the day
        if (lastOnlineTime < fromDate.getTime()) return false;
      }
      
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // End of the day
        if (lastOnlineTime > toDate.getTime()) return false;
      }
    }

    return true;
  });

  const updateFilter = (key, value) => {
    setFilters(prev => {
      const updated = {...prev, [key]: value};
      
      // Make "last seen" and date range filters mutually exclusive
      if (key === 'lastSeen' && value !== 'all') {
        // If last seen is set, clear date range
        updated.dateFrom = '';
        updated.dateTo = '';
      } else if ((key === 'dateFrom' || key === 'dateTo') && value !== '') {
        // If date range is being set, clear last seen
        updated.lastSeen = 'all';
      }
      
      return updated;
    });
  };

  // Calculate hourly activity data
  const hourlyData = calculateHourlyActivity(users);
  const maxCount = Math.max(...hourlyData.map(h => h.count), 1);
  const safeMaxCount = maxCount === 0 ? 1 : maxCount;

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <div className={styles['spinner-text']}>Loading users...</div>
      </div>
    );
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles['users-container']}>
      <div className={styles.hero}>
        <div className={styles.brand}>
          <img src="/logo192.png" alt="Tellmix logo" className={styles['brand-logo']} />
          <div>
          <p className={styles['eyebrow']}>Tellmiz Admin</p>
          <h1>User Intelligence</h1>
          <p className={styles.subhead}>Monitor health, engagement, and trust at a glance.</p>
        </div>
        </div>
        <div className={styles['stat-row']}>
          <div className={styles['stat-card']}>
            <span>Total</span>
            <strong>{users.length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Online</span>
            <strong>{connectedUsers.length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Verified</span>
            <strong>{users.filter(u => u.verified).length}</strong>
          </div>
          <div className={styles['stat-card']}>
            <span>Admins</span>
            <strong>{users.filter(u => u.isAdmin).length}</strong>
          </div>
        </div>
      </div>

      {/* Hourly Activity Chart */}
      <div className={styles['chart-container']}>
        <h2 className={styles['chart-title']}>Users Online - Last 24 Hours</h2>
        <div className={styles['chart-wrapper']}>
          <svg viewBox="0 0 1000 300" className={styles.chart}>
            {/* Grid lines */}
            {[0, 1, 2, 3, 4].map(i => (
              <line
                key={`grid-${i}`}
                x1="50"
                y1={50 + i * 50}
                x2="950"
                y2={50 + i * 50}
                stroke="rgba(0, 45, 98, 0.1)"
                strokeWidth="1"
              />
            ))}
            
            {/* Y-axis labels */}
            {[0, 1, 2, 3, 4].map(i => {
              const value = Math.round(safeMaxCount * (1 - i / 4));
              return (
                <text
                  key={`y-label-${i}`}
                  x="45"
                  y={55 + i * 50}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                >
                  {value}
                </text>
              );
            })}
            
            {/* Chart line */}
            <polyline
              points={hourlyData.map((data, index) => {
                const x = 50 + (index * 900) / (hourlyData.length - 1);
                const y = 250 - (data.count / safeMaxCount) * 200;
                return `${x},${y}`;
              }).join(' ')}
              fill="none"
              stroke="#005499"
              strokeWidth="3"
              className={styles['chart-line']}
            />
            
            {/* Data points */}
            {hourlyData.map((data, index) => {
              const x = 50 + (index * 900) / (hourlyData.length - 1);
              const y = 250 - (data.count / safeMaxCount) * 200;
              return (
                <g key={`point-${index}`}>
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    fill="#005499"
                    className={styles['chart-point']}
                  />
                  {/* Count label above point */}
                  <text
                    x={x}
                    y={y - 10}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#002d62"
                    fontWeight="600"
                  >
                    {data.count}
                  </text>
                  {/* Tooltip on hover */}
                  <title>{`${data.label}: ${data.count} users`}</title>
                </g>
              );
            })}
            
            {/* X-axis labels (show every 4 hours to avoid crowding) */}
            {hourlyData.map((data, index) => {
              if (index % 4 !== 0 && index !== hourlyData.length - 1) return null;
              const x = 50 + (index * 900) / (hourlyData.length - 1);
              return (
                <text
                  key={`x-label-${index}`}
                  x={x}
                  y="285"
                  textAnchor="middle"
                  fontSize="11"
                  fill="#666"
                >
                  {data.label}
                </text>
              );
            })}
          </svg>
        </div>
        <div className={styles['chart-legend']}>
          <span>Peak: {safeMaxCount === 1 && maxCount === 0 ? 0 : maxCount} users</span>
          <span>Total active: {hourlyData.reduce((sum, h) => sum + h.count, 0)} users</span>
        </div>
      </div>

      <div className={styles['glass-panel']}>
        <div className={styles['filters-grid']}>
          <label className={`${styles.filter} ${styles['filter-search']}`}>
            <span>Search</span>
            <input
              type="text"
              placeholder="Search username, email, name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </label>
          <label className={styles.filter}>
            <span>Verification</span>
            <select value={filters.verified} onChange={e => updateFilter('verified', e.target.value)}>
              <option value="all">All</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </label>
          <label className={styles.filter}>
            <span>FCM Tokens</span>
            <select value={filters.fcm} onChange={e => updateFilter('fcm', e.target.value)}>
              <option value="all">All</option>
              <option value="with">With tokens</option>
              <option value="without">Without tokens</option>
            </select>
          </label>
          <label className={styles.filter}>
            <span>Last seen</span>
            <select 
              value={filters.lastSeen} 
              onChange={e => updateFilter('lastSeen', e.target.value)}
              disabled={filters.dateFrom || filters.dateTo}
              title={filters.dateFrom || filters.dateTo ? 'Clear date range filter to use this option' : ''}
            >
              <option value="all">Any time</option>
              <option value="1h">Within 1h</option>
              <option value="4h">Within 4h</option>
              <option value="12h">Within 12h</option>
              <option value="24h">Within 24h</option>
            </select>
          </label>
          <label className={styles.filter}>
            <span>Platform</span>
            <select value={filters.platform} onChange={e => updateFilter('platform', e.target.value)}>
              <option value="all">All</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
              <option value="web">Web</option>
            </select>
          </label>
          <label className={styles.filter}>
            <span>Online From</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={e => updateFilter('dateFrom', e.target.value)}
              disabled={filters.lastSeen !== 'all'}
              title={filters.lastSeen !== 'all' ? 'Clear "Last seen" filter to use date range' : ''}
            />
          </label>
          <label className={styles.filter}>
            <span>Online To</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={e => updateFilter('dateTo', e.target.value)}
              min={filters.dateFrom || undefined}
              disabled={filters.lastSeen !== 'all'}
              title={filters.lastSeen !== 'all' ? 'Clear "Last seen" filter to use date range' : ''}
            />
          </label>
        </div>
        <div className={styles['filter-meta']}>
          <span>{filteredUsers.length} showing</span>
          <div className={styles['online-block']}>
            <span className={styles['online-label']}>Online</span>
            <span className={styles['online-users']}>
              {connectedUsers.map((user) => user.username).join(', ') || ''}
            </span>
          </div>
          <button className={styles['refresh-btn']} onClick={fetchUsers} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className={styles['users-grid']}>
        {filteredUsers.map((user) => (
          <div key={user._id} className={styles['user-card']}>
            <div className={styles['user-header']}>
              <div>
                <div className={styles['user-name']}>{user.username}</div>
                <div className={styles['user-sub']}>{user.email}</div>
              </div>
              <div className={styles['pill-row']}>
                {user.isAdmin && <span className={`${styles.pill} ${styles['pill-strong']}`}>Admin</span>}
                <span className={`${styles.pill} ${user.verified ? styles['pill-ok'] : styles['pill-warn']}`}>
                  {user.verified ? 'Verified' : 'Unverified'}
                </span>
                <span className={styles.pill}>{user.platform || 'Platform n/a'}</span>
              </div>
            </div>

            <div className={styles['user-meta']}>
              <span><strong>Last seen:</strong> {formatLastSeenHours(user.lastOnline)}</span>
              <span><strong>App:</strong> {user.appVersion || '—'}</span>
              <span><strong>Friends:</strong> {user.friends?.length ?? 0}</span>
              <span><strong>Fcm:</strong> {user.fcmTokens.length}</span>
            </div>

            <div className={styles['user-meta']}>
              <span><strong>Name:</strong> {user.firstName} {user.lastName}</span>
              <span><strong>Created:</strong> {new Date(user.createdAt).toLocaleDateString()}</span>
            </div>

            <div className={styles['user-meta']}>
              <span><strong>Sent req:</strong> {user.sentFriendRequests?.length ?? 0}</span>
              <span><strong>Received req:</strong> {user.receivedFriendRequests?.length ?? 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsersList;
